// # Give Assignment Component for Step 2
import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuthStore } from '../../../auth/state';
import SharedHeader from '../../../components/SharedHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { FaCheckCircle, FaCheckDouble, FaTimes, FaCheck, FaExclamationCircle } from 'react-icons/fa';
import './give_assign2.css';

// # Import icons
import anatomyIcon from '../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../general/library/step1/step1_icons/blood.png';
import cnsIcon from '../../../general/library/step1/step1_icons/cns.png';
import earIcon from '../../../general/library/step1/step1_icons/ear.png';
import endocrineIcon from '../../../general/library/step1/step1_icons/endocrine.png';
import eyeIcon from '../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../general/library/step1/step1_icons/git.png';
import healthcareIcon from '../../../general/library/step1/step1_icons/healthcare.png';
import heartIcon from '../../../general/library/step1/step1_icons/heart.png';
import immunologyIcon from '../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../general/library/step1/step1_icons/infectious.png';
import kidneyIcon from '../../../general/library/step1/step1_icons/kidney.png';
import lungsIcon from '../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../general/library/step1/step1_icons/musculoskeletal.png';
import newbornIcon from '../../../general/library/step1/step1_icons/newborn.png';
import pharmaIcon from '../../../general/library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../../../general/library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../../../general/library/step1/step1_icons/public_health.png';
import qualityIcon from '../../../general/library/step1/step1_icons/quality.png';
import reproductiveIcon from '../../../general/library/step1/step1_icons/reproductive-system.png';
import skinIcon from '../../../general/library/step1/step1_icons/skin.png';
import statisticsIcon from '../../../general/library/step1/step1_icons/statistics.png';
import surgeryIcon from '../../../general/library/step1/step1_icons/surgery.png';
import emergencyIcon from '../../../general/library/step1/step1_icons/emergency.png';
import wellPatientIcon from '../../../general/library/step1/step1_icons/well_patient.png';

// # Give Assignment component for Step 2
function GiveAssignment2({ isAdmin }) {
  // # Get user info from auth store
  const { user } = useAuthStore();
  
  // # State for modal and form
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [assignmentDate, setAssignmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [assignedChapters, setAssignedChapters] = useState({});
  const [chapterStatus, setChapterStatus] = useState({});
  const [isChapterDetailsOpen, setIsChapterDetailsOpen] = useState(false);
  const [selectedChapterDetails, setSelectedChapterDetails] = useState(null);
  const [alert, setAlert] = useState(null);
  // # Add state for target user ID and mentor status
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);

  // # Chapter data with standardized titles
  const chapters = [
    { id: 1, title: 'Cardiovascular', icon: heartIcon, color: '#FFD6E0' }, // # Light pink
    { id: 2, title: 'Respiratory & ENT', icon: lungsIcon, color: '#FFEFB5' }, // # Light yellow
    { id: 3, title: 'Renal & Urology', icon: kidneyIcon, color: '#D6F5D6' }, // # Light green
    { id: 4, title: 'Endocrine', icon: endocrineIcon, color: '#D6E6FF' }, // # Light blue
    { id: 5, title: 'Hematology & Oncology', icon: bloodIcon, color: '#E6D6FF' }, // # Light purple
    { id: 6, title: 'Immunology', icon: immunologyIcon, color: '#FFE0CC' }, // # Light orange
    { id: 7, title: 'Gastrointestinal', icon: gitIcon, color: '#D6FFFF' }, // # Light cyan
    { id: 8, title: 'Musculoskeletal', icon: musculoskeletalIcon, color: '#FFD6E0' }, // # Light pink
    { id: 9, title: 'Neurology', icon: cnsIcon, color: '#FFEFB5' }, // # Light yellow
    { id: 10, title: 'Ophthalmology', icon: eyeIcon, color: '#D6F5D6' }, // # Light green
    { id: 11, title: 'Psychiatry', icon: psychiatryIcon, color: '#D6E6FF' }, // # Light blue
    { id: 12, title: 'Dermatology', icon: skinIcon, color: '#E6D6FF' }, // # Light purple
    { id: 13, title: 'Gynecology & Obstetrics', icon: reproductiveIcon, color: '#FFE0CC' }, // # Light orange
    { id: 14, title: 'Neonate & Infant', icon: newbornIcon, color: '#D6FFFF' }, // # Light cyan
    { id: 15, title: 'Genetics', icon: biochemistryIcon, color: '#FFD6E0' }, // # Light pink
    { id: 16, title: 'Infectious Disease', icon: infectiousIcon, color: '#FFEFB5' }, // # Light yellow
    { id: 17, title: 'Surgery & Anesthesia', icon: surgeryIcon, color: '#D6F5D6' }, // # Light green
    { id: 18, title: 'Emergency, Trauma & Toxicology', icon: emergencyIcon, color: '#D6E6FF' }, // # Light blue
    { id: 19, title: 'The Well Patient', icon: wellPatientIcon, color: '#E6D6FF' }, // # Light purple
    { id: 20, title: 'Biostatistics & Epidemiology', icon: statisticsIcon, color: '#FFE0CC' }, // # Light orange
    { id: 21, title: 'Ethics & Communication', icon: publicHealthIcon, color: '#D6FFFF' }, // # Light cyan
    { id: 22, title: 'Healthcare Delivery', icon: healthcareIcon, color: '#FFD6E0' }, // # Light pink
    { id: 23, title: 'Quality & Safety', icon: qualityIcon, color: '#FFEFB5' }, // # Light yellow
    { id: 24, title: 'Pharmacology Review', icon: pharmaIcon, color: '#D6F5D6' } // # Light green
  ];

  // # Get the correct user ID (mentee's ID when mentor is viewing)
  useEffect(() => {
    const menteeId = sessionStorage.getItem('currentViewingMenteeId');
    setTargetUserId(menteeId || user?.uid);
    setIsMentor(!!menteeId);
  }, [user?.uid]);

  // # Update fetchChapterStatus to use targetUserId
  useEffect(() => {
    const fetchChapterStatus = async () => {
      try {
        if (!targetUserId) return;
        
        const db = getFirestore();
        const menteeRef = doc(db, 's2_mentee', targetUserId);
        const menteeDoc = await getDoc(menteeRef);
        
        if (menteeDoc.exists()) {
          const assignments = menteeDoc.data()?.assignments || {};
          const assigned = {};
          const status = {};
          
          Object.keys(assignments).forEach(chapter => {
            if (assignments[chapter]?.metadata?.assigned) {
              assigned[chapter] = true;
              status[chapter] = {
                complete: assignments[chapter]?.metadata?.complete || false,
                retired: assignments[chapter]?.metadata?.retired || false
              };
            }
          });
          
          setAssignedChapters(assigned);
          setChapterStatus(status);
        }
      } catch (error) {
        console.error('Error fetching assignments:', error);
      }
    };

    if (targetUserId) {
      fetchChapterStatus();
    }
  }, [targetUserId]);

  // # Handle assign button click
  const handleAssignClick = () => {
    setIsModalOpen(true);
  };

  // # Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedChapter(null);
  };

  // # Handle chapter selection in modal
  const handleChapterSelect = (chapter) => {
    setSelectedChapter(chapter);
  };

  // # Handle date change
  const handleDateChange = (e) => {
    setAssignmentDate(e.target.value);
  };

  // # Handle chapter tile click
  const handleChapterClick = async (chapter) => {
    try {
      const db = getFirestore();
      const menteeRef = doc(db, 's2_mentee', user.uid);
      const menteeDoc = await getDoc(menteeRef);
      
      if (menteeDoc.exists()) {
        const assignments = menteeDoc.data()?.assignments || {};
        const chapterData = assignments[chapter.title] || {
          metadata: {},
          corrects: {},
          mistakes: {}
        };
        
        setSelectedChapterDetails({
          ...chapter,
          data: chapterData,
          totalQuestions: Object.keys(chapterData.corrects).length + Object.keys(chapterData.mistakes).length,
          correctCount: Object.keys(chapterData.corrects).length,
          mistakeCount: Object.keys(chapterData.mistakes).length,
          correctPercent: calculatePercentage(
            Object.keys(chapterData.corrects).length,
            Object.keys(chapterData.corrects).length + Object.keys(chapterData.mistakes).length
          )
        });
        setIsChapterDetailsOpen(true);
      }
    } catch (error) {
      console.error('Error fetching chapter details:', error);
    }
  };

  // # Calculate percentage helper
  const calculatePercentage = (correct, total) => {
    if (total === 0) return 0;
    return Math.round((correct / total) * 100);
  };

  // # Show alert helper function
  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000); // Hide after 3 seconds
  };

  // # Handle assignment confirmation
  const handleConfirmAssignment = async () => {
    if (!selectedChapter || !targetUserId) return;

    try {
      const db = getFirestore();
      const menteeRef = doc(db, 's2_mentee', targetUserId);

      const assignmentData = {
        metadata: {
          given_at: new Date().toISOString(),
          given_by: user.email,
          assigned: true,
          complete: false
        },
        corrects: {},
        mistakes: {}
      };

      await updateDoc(menteeRef, {
        [`assignments.${selectedChapter.title}`]: assignmentData
      });

      setAssignedChapters(prev => ({
        ...prev,
        [selectedChapter.title]: true
      }));
      setChapterStatus(prev => ({
        ...prev,
        [selectedChapter.title]: { complete: false }
      }));

      handleModalClose();
      showAlert('Chapter assigned successfully!');
    } catch (error) {
      console.error('Error creating assignment:', error);
      showAlert('Failed to create assignment. Please try again.', 'error');
    }
  };

  // # Handle retire chapter
  const handleRetireChapter = async () => {
    if (!selectedChapterDetails || !targetUserId) return;

    try {
      const db = getFirestore();
      const menteeRef = doc(db, 's2_mentee', targetUserId);

      await updateDoc(menteeRef, {
        [`assignments.${selectedChapterDetails.title}.metadata.retired`]: true
      });

      setChapterStatus(prev => ({
        ...prev,
        [selectedChapterDetails.title]: {
          ...prev[selectedChapterDetails.title],
          retired: true
        }
      }));

      setIsChapterDetailsOpen(false);
      showAlert('Chapter retired successfully!');
    } catch (error) {
      console.error('Error retiring chapter:', error);
      showAlert('Failed to retire chapter. Please try again.', 'error');
    }
  };

  // # Handle unretire chapter
  const handleUnretireChapter = async () => {
    if (!selectedChapterDetails || !targetUserId) return;

    try {
      const db = getFirestore();
      const menteeRef = doc(db, 's2_mentee', targetUserId);

      await updateDoc(menteeRef, {
        [`assignments.${selectedChapterDetails.title}.metadata.retired`]: false
      });

      setChapterStatus(prev => ({
        ...prev,
        [selectedChapterDetails.title]: {
          ...prev[selectedChapterDetails.title],
          retired: false
        }
      }));

      setIsChapterDetailsOpen(false);
      showAlert('Chapter unretired successfully!');
    } catch (error) {
      console.error('Error unretiring chapter:', error);
      showAlert('Failed to unretire chapter. Please try again.', 'error');
    }
  };

  // # Handle unassign chapter
  const handleUnassignChapter = async () => {
    if (!selectedChapterDetails || !targetUserId) return;

    try {
      const db = getFirestore();
      const menteeRef = doc(db, 's2_mentee', targetUserId);

      await updateDoc(menteeRef, {
        [`assignments.${selectedChapterDetails.title}`]: {}
      });

      setAssignedChapters(prev => {
        const newState = { ...prev };
        delete newState[selectedChapterDetails.title];
        return newState;
      });

      setChapterStatus(prev => {
        const newState = { ...prev };
        delete newState[selectedChapterDetails.title];
        return newState;
      });

      setIsChapterDetailsOpen(false);
      showAlert('Chapter unassigned successfully!');
    } catch (error) {
      console.error('Error unassigning chapter:', error);
      showAlert('Failed to unassign chapter. Please try again.', 'error');
    }
  };

  return (
    <div className="gv_asgn2_container">
      {alert && (
        <div className={`gv_asgn2_alert ${alert.type === 'success' ? 'gv_asgn2_alert_success' : 'gv_asgn2_alert_error'}`}>
          <span className="gv_asgn2_alert_icon">
            {alert.type === 'success' ? <FaCheck /> : <FaExclamationCircle />}
          </span>
          <p className="gv_asgn2_alert_message">{alert.message}</p>
        </div>
      )}
      <SharedHeader />
      <div className="gv_asgn2_content">
        <div className="gv_asgn2_header">
          <h1>USMLE Step 2 Assignments {isMentor && <span className="gv_asgn2_mentor_pill">Mentor</span>}</h1>
          <p>Select a chapter to create a new assignment</p>
        </div>

        {/* # Assignment Form */}
        <div className="gv_asgn2_assignment_form">
          <h2>Create New Assignment</h2>
          <button 
            className="gv_asgn2_confirm_btn"
            onClick={handleAssignClick}
          >
            Assign New Chapter
          </button>
        </div>

        {/* # Chapters Grid */}
        <div className="gv_asgn2_chapters_grid">
          {chapters.map((chapter) => (
            <div 
              key={chapter.id}
              className="gv_asgn2_chapter_tile"
              style={{ backgroundColor: chapter.color }}
              onClick={() => handleChapterClick(chapter)}
            >
              <div className="gv_asgn2_chapter_icon">
                <img 
                  src={chapter.icon} 
                  alt={chapter.title} 
                  className="gv_asgn2_icon_image"
                />
              </div>
              <h3 className="gv_asgn2_chapter_title">{chapter.title}</h3>
              {assignedChapters[chapter.title] && (
                <div className="gv_asgn2_assigned_icon">
                  <FaCheckCircle />
                </div>
              )}
              {chapterStatus[chapter.title]?.complete && (
                <div className="gv_asgn2_complete_icon">
                  <FaCheckDouble />
                </div>
              )}
              {chapterStatus[chapter.title]?.retired && (
                <div className="gv_asgn2_retired_icon">
                  <FaTimes />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* # Chapter Details Modal */}
        {isChapterDetailsOpen && selectedChapterDetails && (
          <div className="gv_asgn2_modal_overlay">
            <div className="gv_asgn2_details_modal">
              <div className="gv_asgn2_details_header" style={{ backgroundColor: selectedChapterDetails.color + '40' }}>
                <div className="gv_asgn2_details_icon">
                  <img src={selectedChapterDetails.icon} alt={selectedChapterDetails.title} />
                </div>
                <h2>{selectedChapterDetails.title}</h2>
                <button 
                  className="gv_asgn2_close_btn"
                  onClick={() => setIsChapterDetailsOpen(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="gv_asgn2_details_content">
                <div className="gv_asgn2_stats_grid">
                  <div className="gv_asgn2_stat_item">
                    <h3>Total Questions</h3>
                    <p>{selectedChapterDetails.totalQuestions}</p>
                  </div>
                  <div className="gv_asgn2_stat_item">
                    <h3>Correct Answers</h3>
                    <p className="gv_asgn2_correct">{selectedChapterDetails.correctCount}</p>
                  </div>
                  <div className="gv_asgn2_stat_item">
                    <h3>Mistakes</h3>
                    <p className="gv_asgn2_mistake">{selectedChapterDetails.mistakeCount}</p>
                  </div>
                  <div className="gv_asgn2_stat_item">
                    <h3>Correct Percentage</h3>
                    <p className="gv_asgn2_percent">{selectedChapterDetails.correctPercent}%</p>
                  </div>
                </div>

                <div className="gv_asgn2_metadata">
                  <h3>Metadata</h3>
                  <div className="gv_asgn2_metadata_content">
                    <p><strong>Assigned:</strong> {selectedChapterDetails.data.metadata.assigned ? 'Yes' : 'No'}</p>
                    <p><strong>Given At:</strong> {new Date(selectedChapterDetails.data.metadata.given_at).toLocaleDateString()}</p>
                    <p><strong>Given By:</strong> {selectedChapterDetails.data.metadata.given_by}</p>
                    <p><strong>Status:</strong> {selectedChapterDetails.data.metadata.retired ? 'Retired' : 'Active'}</p>
                  </div>
                </div>

                <div className="gv_asgn2_details_actions">
                  {assignedChapters[selectedChapterDetails.title] && (
                    <>
                      {chapterStatus[selectedChapterDetails.title]?.retired ? (
                        <button 
                          className="gv_asgn2_unretire_btn"
                          onClick={handleUnretireChapter}
                        >
                          Unretire Chapter
                        </button>
                      ) : (
                        <button 
                          className="gv_asgn2_retire_btn"
                          onClick={handleRetireChapter}
                        >
                          Retire Chapter
                        </button>
                      )}
                      <button 
                        className="gv_asgn2_unassign_btn"
                        onClick={handleUnassignChapter}
                      >
                        Unassign Chapter
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* # Assignment Modal */}
        {isModalOpen && (
          <div className="gv_asgn2_modal_overlay">
            <div className="gv_asgn2_modal">
              <div className="gv_asgn2_modal_header">
                <h2>Select Chapter to Assign</h2>
              </div>
              
              <div className="gv_asgn2_chapter_list">
                {chapters
                  .filter(chapter => !assignedChapters[chapter.title]) // # Filter out assigned chapters
                  .map((chapter) => (
                    <div
                      key={chapter.id}
                      className={`gv_asgn2_chapter_option ${selectedChapter?.id === chapter.id ? 'selected' : ''}`}
                      onClick={() => handleChapterSelect(chapter)}
                      style={{ backgroundColor: selectedChapter?.id === chapter.id ? `${chapter.color}40` : 'transparent' }}
                    >
                      <div className="gv_asgn2_chapter_icon">
                        <img src={chapter.icon} alt={chapter.title} className="gv_asgn2_icon_image" />
                      </div>
                      <span>{chapter.title}</span>
                    </div>
                  ))}
                {chapters.filter(chapter => !assignedChapters[chapter.title]).length === 0 && (
                  <div className="gv_asgn2_no_chapters">
                    <p>All chapters have been assigned</p>
                  </div>
                )}
              </div>

              <div className="gv_asgn2_date_picker">
                <label htmlFor="assignmentDate">Assignment Date</label>
                <input
                  type="date"
                  id="assignmentDate"
                  value={assignmentDate}
                  onChange={handleDateChange}
                />
              </div>

              <div className="gv_asgn2_modal_buttons">
                <button 
                  className="gv_asgn2_cancel_btn"
                  onClick={handleModalClose}
                >
                  Cancel
                </button>
                <button 
                  className="gv_asgn2_confirm_btn"
                  onClick={handleConfirmAssignment}
                  disabled={!selectedChapter}
                >
                  Confirm Assignment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <DashboardFooter />
    </div>
  );
}

export default GiveAssignment2;
